import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { FindingsProvider } from "./contexts/FindingsContext";
import { SubdomainMonitorBadgeProvider } from "./contexts/SubdomainMonitorBadgeContext";
import LoginForm from "./components/Auth/LoginForm";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ResetPassword from "./components/Auth/ResetPassword";
import VerifyEmail from "./components/Auth/VerifyEmail";
import SsoCallback from "./components/Auth/SsoCallback";
import MobileNotice from "./components/common/MobileNotice";
import TopNav from "./components/Layout/TopNav";
import Intelligence from "./components/Intelligence/Intelligence";
import Settings from "./components/Settings/Settings";
import TargetDetail from "./pages/TargetDetail";
import ScanResultsPage from "./pages/ScanResults";
import BeaconManagement from "./components/Beacons/BeaconManagement";
import UsersPage from "./components/Users/UsersPage";
import PentestList from "./components/Pentests/PentestList";
import PentestDetail from "./components/Pentests/PentestDetail";
import StatusPage from "./components/StatusPage/StatusPage";
import PortfolioDashboard from "./components/Dashboard/PortfolioDashboard";
import SubdomainMonitorList from "./components/SubdomainMonitors/SubdomainMonitorList";
import SubdomainMonitorDetail from "./components/SubdomainMonitors/SubdomainMonitorDetail";
import OsintLeaks from "./components/OSINT/Leaks";
import AccountDetail from "./components/OSINT/AccountDetail";
import AccountsBrowser from "./components/OSINT/AccountsBrowser";
import LeakSiteSweep from "./components/OSINT/LeakSiteSweep";
import AssetInventory from "./components/Monitor/AssetInventory";
import VulnerabilityFindings from "./components/Monitor/VulnerabilityFindings";

// Agent Pentesting: a standalone QA/red-team product for testing a
// customer's LLM agent (not part of the Pentests/PT plane). Lazy-loaded
// since it is a self-contained section most sessions never visit.
const AgentPentestList = React.lazy(() => import("./components/AgentPentesting/AgentPentestList"));
const AgentPentestDetail = React.lazy(() => import("./components/AgentPentesting/AgentPentestDetail"));
const AgentPentestRun = React.lazy(() => import("./components/AgentPentesting/AgentPentestRun"));

const RouteLoading = () => (
  <div className="flex items-center justify-center py-24">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shinobi-green"></div>
  </div>
);

// Capability-based route guard. Disallowed roles bounce to /dashboard (open to
// all). Role sets mirror the backend RBAC tiers.
const OPERATOR_ROLES = ["pentester", "blue_team"]; // Red + Blue Teamer
const OFFENSIVE_ROLES = ["pentester", "wasabian"]; // Red Teamer + read-only Wasabian
const ADMIN_ROLES = ["pentester"]; // Red Teamer only
const AGENT_PENTEST_ROLES = ["pentester"]; // Agent Pentesting: Red Teamer only

const RoleRoute = ({ allow, children }) => {
  const { role } = useAuth();
  return allow.includes(role) ? children : <Navigate to="/dashboard" replace />;
};

const MOBILE_NOTICE_STORAGE_KEY = "shinobi-mobile-notice-seen";

const MainApp = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const [mobileNoticeOpen, setMobileNoticeOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || loading) return;
    try {
      const seen = localStorage.getItem(MOBILE_NOTICE_STORAGE_KEY);
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      if (isMobile && !seen) {
        setMobileNoticeOpen(true);
      }
    } catch (e) {}
  }, [isAuthenticated, loading]);

  const handleCloseMobileNotice = () => {
    try {
      localStorage.setItem(MOBILE_NOTICE_STORAGE_KEY, "1");
    } catch (e) {}
    setMobileNoticeOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cyber-50 cyber-grid-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shinobi-purple mx-auto"></div>
          <p className="mt-4 text-gray-400">Initializing...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/sso/callback" element={<SsoCallback />} />
        <Route path="*" element={<LoginForm />} />
      </Routes>
    );
  }

  return (
    <div className="h-screen bg-surface flex flex-col overflow-hidden">
      <TopNav />
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* Dashboard + Settings: open to all roles. */}
          <Route path="/dashboard" element={<PortfolioDashboard />} />
          {/* Offensive plane: Red Teamer + Wasabian (read). Blue Teamer blocked. */}
          <Route path="/pentests" element={<RoleRoute allow={OFFENSIVE_ROLES}><PentestList /></RoleRoute>} />
          <Route path="/pentests/new" element={<Navigate to="/pentests" replace />} />
          <Route path="/pentests/:id/*" element={<RoleRoute allow={OFFENSIVE_ROLES}><PentestDetail /></RoleRoute>} />
          <Route path="/status" element={<RoleRoute allow={OFFENSIVE_ROLES}><StatusPage /></RoleRoute>} />
          <Route path="/reports" element={<Navigate to="/pentests" replace />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />

          {/* Red Teamer (Pentester) only */}
          <Route path="/users" element={<RoleRoute allow={ADMIN_ROLES}><UsersPage /></RoleRoute>} />
          <Route path="/targets/:id" element={<RoleRoute allow={ADMIN_ROLES}><TargetDetail /></RoleRoute>} />
          <Route path="/scans/:id/results" element={<RoleRoute allow={ADMIN_ROLES}><ScanResultsPage /></RoleRoute>} />
          <Route path="/intelligence" element={<RoleRoute allow={ADMIN_ROLES}><Intelligence /></RoleRoute>} />

          {/* Monitoring operator plane: Red + Blue Teamer */}
          <Route path="/beacons" element={<RoleRoute allow={OPERATOR_ROLES}><BeaconManagement /></RoleRoute>} />
          <Route path="/subdomain-monitors" element={<RoleRoute allow={OPERATOR_ROLES}><SubdomainMonitorList /></RoleRoute>} />
          <Route path="/subdomain-monitors/:id" element={<RoleRoute allow={OPERATOR_ROLES}><SubdomainMonitorDetail /></RoleRoute>} />
          <Route path="/osint/leaks" element={<RoleRoute allow={OPERATOR_ROLES}><OsintLeaks /></RoleRoute>} />
          <Route path="/osint/leaks/accounts" element={<RoleRoute allow={OPERATOR_ROLES}><AccountsBrowser /></RoleRoute>} />
          <Route path="/osint/leaks/accounts/:account" element={<RoleRoute allow={OPERATOR_ROLES}><AccountDetail /></RoleRoute>} />
          <Route path="/osint/leaks/:searchId" element={<RoleRoute allow={OPERATOR_ROLES}><OsintLeaks /></RoleRoute>} />
          <Route path="/osint/leak-sweep" element={<RoleRoute allow={OPERATOR_ROLES}><LeakSiteSweep /></RoleRoute>} />
          {/* Asset Inventory + Vulnerability Findings: all roles (Wasabian read-only). */}
          <Route path="/monitor/assets" element={<AssetInventory />} />
          <Route path="/monitor/findings" element={<VulnerabilityFindings />} />
          <Route path="/ai-insights" element={<Navigate to="/pentests" replace />} />

          {/* Agent Pentesting: standalone LLM-agent QA/red-team product, Red Teamer only. */}
          <Route
            path="/agent-pentesting"
            element={
              <RoleRoute allow={AGENT_PENTEST_ROLES}>
                <React.Suspense fallback={<RouteLoading />}>
                  <AgentPentestList />
                </React.Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/agent-pentesting/:id"
            element={
              <RoleRoute allow={AGENT_PENTEST_ROLES}>
                <React.Suspense fallback={<RouteLoading />}>
                  <AgentPentestDetail />
                </React.Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/agent-pentesting/:id/runs/:runId"
            element={
              <RoleRoute allow={AGENT_PENTEST_ROLES}>
                <React.Suspense fallback={<RouteLoading />}>
                  <AgentPentestRun />
                </React.Suspense>
              </RoleRoute>
            }
          />

          {/* Redirects for legacy routes (Phase 2 cleanup pass: dead pages now point to /pentests) */}
          <Route path="/targets" element={<Navigate to="/pentests" replace />} />
          <Route path="/engagements" element={<Navigate to="/pentests" replace />} />
          <Route path="/engagements/:id" element={<Navigate to="/pentests" replace />} />
          <Route path="/arsenal" element={<Navigate to="/pentests" replace />} />
          <Route path="/intelligence/map" element={<Navigate to="/pentests" replace />} />
          <Route path="/assets" element={<Navigate to="/pentests" replace />} />
          <Route path="/vulnerabilities" element={<Navigate to="/pentests" replace />} />
          <Route path="/reconnaissance" element={<Navigate to="/pentests" replace />} />
          <Route path="/compliance" element={<Navigate to="/pentests" replace />} />
          <Route path="/intelligence/nis2" element={<Navigate to="/pentests" replace />} />
          <Route path="/network-topology" element={<Navigate to="/pentests" replace />} />
          <Route path="/threat-intel" element={<Navigate to="/pentests" replace />} />
          <Route path="/threat-intel/feeds" element={<Navigate to="/pentests" replace />} />
        </Routes>
      </div>

      {mobileNoticeOpen && <MobileNotice onClose={handleCloseMobileNotice} />}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FindingsProvider>
          <SubdomainMonitorBadgeProvider>
            <MainApp />
          </SubdomainMonitorBadgeProvider>
        </FindingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
